<template>
  <div class="bill-list">
    <div v-if="loading">
      <list-item loading v-for="(item, index) in 10" :key="index"></list-item>
    </div>
    <div v-else>
      <RecycleScroller
        :items="groupedBills"
        :buffer="1000"
        key-field="listId"
        page-mode
        v-slot="{ item }"
      >
        <v-modal
          animation="slide-up"
          block-scroll
          :min-width="!screen.breakPoint.tabPort ? null: '56rem'"
          :full="!screen.breakPoint.tabPort"
          :noBackground="!screen.breakPoint.tabPort"
          theme="light-grey"
          handle-click-bg
          :max-body-height="screen.breakPoint.tabPort ? '70vh' : null"
          class="bill-list__modal"
        >
          <template v-slot:activator="{ on }">
            <list-item two-columns @click="openModal(on, item)">
              <template v-slot:groupTitle>
                <div v-if="item.groupDate">
                  {{ item.groupDate }}
                </div>
              </template>

              <template v-slot:firstColumn>
                <list-item-group>
                  <list-item-content>
                    <list-item-title :cut-to-length="screen.breakPoint.bigDesktop ? 45 : 20">
                      {{$t('views.Bills.invoice')}} {{ item.number ? '№' + item.number : null }}
                    </list-item-title>

                    <list-item-subtitle>
                      <span :class="item.status !== '2' && item.status !== '3' ? 'text-color-error' : ''">{{ operationTypes[item.status] }}</span>
                    </list-item-subtitle>
                  </list-item-content>
                </list-item-group>
              </template>

              <template v-slot:secondColumn>
                <list-item-title font-medium>
                  <span :class="item.status !== '2' && item.status !== '3' ? 'text-color-error' : ''">
                    {{ toCurrency(item.debtAmount, item.currency) }}
                  </span>
                </list-item-title>
                <list-item-subtitle>
                  {{ toCurrency(item.amount, item.currency) }}
                </list-item-subtitle>
              </template>

              <template v-slot:controls>
                <kebab-menu>
                  <action-link icon-left="icon-download" size="small" @click="downloadInvoice(item)"><span v-html="$t('views.Bills.downloadBill')"></span></action-link>
                </kebab-menu>
              </template>
            </list-item>
          </template>

          <template v-slot:header>
            {{$t('views.Bills.ordersByInvoice')}}
          </template>

          <div class="bill-list__orders">
            <orders-list date-field="orderedAt" :orders="orders ? orders.content : []" :loading="isLoading"></orders-list>
            <list-footer :loading="fetchingMore || $apollo.queries.orders.loading" :total="!orders ? 0 : orders.totalElements" class="mb-2" short></list-footer>
          </div>
        </v-modal>
      </RecycleScroller>
    </div>
  </div>
</template>

<script>
import ListItemGroup from '@/ui/slots/List/ListItemGroup'
import ListItem from '@/ui/slots/List/ListItem'
import ListItemContent from '@/ui/slots/List/ListItemContent'
import ListItemTitle from '@/ui/slots/List/ListItemTitle'
import ListItemSubtitle from '@/ui/slots/List/ListItemSubtitle'
import ListFooter from '@/ui/slots/List/ListFooter'

import KebabMenu from '@/ui/controls/KebabMenu'
import ActionLink from '@/ui/controls/ActionLink'

import OrdersList from '@/components/OrdersList'

import vModal from '@/ui/modals/vModal'

import groupByDate from '@/mixins/groupByDate'
import toCurrency from '@/mixins/toCurrency'
import screen from '@/mixins/screen'
import toSimpleDate from '@/mixins/toSimpleDate'
import fetchMore from '@/mixins/fetchMore'

import { OrdersGQL } from '@/graphql/queries'

const now = new Date()
/**
 * @param {Date} nowDate
 */
const threeMonthAgo = () => {
  const date = new Date()

  if (date.getMonth() === 0) {
    return new Date(date.getFullYear() - 1, 10, 1)
  } else {
    return new Date(date.getFullYear(), date.getMonth() - 2, 1)
  }
}

const queryVars = {
  descending: true,
  orderedAtFrom: threeMonthAgo().toISOString(),
  orderedAtTo: now.toISOString(),
  sortBy: 'ORDERED_AT'
}

const paginationVars = {
  page: 0,
  size: 20
}

const activeFilter = {
  value: null,
  name: 'Все'
}

export default {
  components: {
    ListItem,
    ListItemGroup,
    ListItemContent,
    ListItemTitle,
    ListItemSubtitle,
    ListFooter,

    OrdersList,

    KebabMenu,
    ActionLink,

    vModal
  },

  mixins: [groupByDate, toCurrency, screen, toSimpleDate, fetchMore],

  data () {
    return {
      currShopBillId: null
    }
  },

  apollo: {
    orders: {
      query: OrdersGQL,
      variables () {
        return {
          params: {
            ...queryVars,
            ...paginationVars,
            status: activeFilter.value,
            contractId: this.currContractId,
            shopBillId: this.currShopBillId
          }
        }
      },
      update: data => data.merchant.orders,
      skip () {
        return !this.currContractId && !this.currShopBillId
      }
    }
  },

  props: {
    loadingItems: {
      type: Number,
      default: 10
    },
    dateField: {
      type: String,
      default: 'date'
    },
    bills: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    groupedBills () {
      return this.groupByDate(
        this.bills.map(b => {
          return {
            ...b,
            url: this.$getEnv.billPath + b.url
          }
        }),
        this.dateField
      )
    },

    documentTypes () {
      return {
        order: this.$i18n.t('components.BillList.documentTypes.order'),
        invoice: this.$i18n.t('components.BillList.documentTypes.invoice'),
        payment: this.$i18n.t('components.BillList.documentTypes.payment'),
        goldshop: this.$i18n.t('components.BillList.documentTypes.goldshop'),
        other: this.$i18n.t('components.BillList.documentTypes.other')
      }
    },

    operationTypes () {
      return [this.$i18n.t('components.BillList.status.NotPaid'), this.$i18n.t('components.BillList.status.PartiallyPaid'), this.$i18n.t('components.BillList.status.Paid'), this.$i18n.t('components.BillList.status.Generating')]
    },

    isLoading () {
      return (!this.orders && true) || (!this.fetchingMore && this.$apollo.queries.orders && this.$apollo.queries.orders.loading)
    },
    loadingMore () {
      return this.orders && this.$apollo.queries.orders && this.$apollo.queries.orders.loading && this.fetchingMore
    }
  },

  methods: {
    openModal (callback, item) {
      this.currShopBillId = item.id
      callback()
    },

    downloadInvoice (item) {
      if (item && item.url) {
        window.open(item.url, '_blank')
      }
    },
    showMore () {
      const params = {
        ...queryVars,
        ...paginationVars,
        status: activeFilter.value,
        contractId: this.currContractId,
        shopBillId: this.currShopBillId
      }

      if (this.orders) {
        this.fetchMore({
          params,
          rootField: 'merchant',
          apolloQueryField: 'orders',
          currPage: this.orders.currentPage
        })
      }
    }
  },
  mounted () {
    document.addEventListener('reachedBottom', this.showMore)
  },

  beforeDestroy () {
    document.removeEventListener('reachedBottom', this.showMore)
  }
}
</script>
